import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWarehouseMap, placePallet } from '@/lib/api';
import { useWMSEvents } from '@/lib/websocket';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { MapPin, ChevronRight, Package, Layers, Grid3X3, ArrowRight } from 'lucide-react';
import { ROW_COLORS, CATEGORY_COLORS, STATUS_COLORS, getAgeColor, AGE_COLORS } from '@/lib/constants';

// Rack config: A01-A09 have 6 levels, all others have 4
const TALL_RACKS = ['A01', 'A02', 'A03', 'A04', 'A05', 'A06', 'A07', 'A08', 'A09'];

export default function WarehouseMapPage() {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRack, setSelectedRack] = useState(null);
  const [heatmapMode, setHeatmapMode] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [rackPanelOpen, setRackPanelOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadMap(); }, []);

  // Real-time map updates via WebSocket
  const handleMapUpdate = useCallback(() => {
    loadMap();
  }, []);

  useWMSEvents({
    onMapUpdate: handleMapUpdate,
    pallet_placed: handleMapUpdate,
    location_move: handleMapUpdate,
    qc_hold_placed: handleMapUpdate,
    qc_hold_cleared: handleMapUpdate,
    pallet_issued: handleMapUpdate,
    conflict_detected: handleMapUpdate,
  });

  const loadMap = async () => {
    try {
      const res = await getWarehouseMap();
      setMapData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-[#1a2fce] border-t-transparent rounded-full" /></div>;

  const rows = mapData?.rows || {};
  const palletsBySlot = mapData?.pallets_by_slot || {};

  const getRackOccupancy = (rackCode) => {
    const rowKey = rackCode[0];
    const rackData = rows[rowKey]?.racks[rackCode];
    if (!rackData) return { ratio: 0, occupied: 0, total: 0 };
    const slots = rackData.slots;
    let occupied = 0;
    slots.forEach(s => { if (palletsBySlot[s.slot_address]?.length > 0) occupied++; });
    return {
      ratio: slots.length > 0 ? occupied / slots.length : 0,
      occupied,
      total: slots.length
    };
  };

  const getOccupancyColor = (ratio, row) => {
    if (ratio === 0) return '#e5e7eb';
    const base = ROW_COLORS[row] || '#1a2fce';
    if (ratio <= 0.5) return `${base}40`;
    if (ratio <= 0.9) return `${base}80`;
    return base;
  };

  const openRackPanel = (rackCode) => {
    setSelectedRack(rackCode);
    setRackPanelOpen(true);
  };

  // Get rack info for panel
  const getRackInfo = () => {
    if (!selectedRack) return null;
    const rowKey = selectedRack[0];
    const rackInfo = rows[rowKey]?.racks[selectedRack];
    if (!rackInfo) return null;
    const occ = getRackOccupancy(selectedRack);
    return { ...rackInfo, ...occ, row: rowKey };
  };

  const rackInfo = getRackInfo();

  return (
    <div className="space-y-4" data-testid="warehouse-map">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Warehouse Map</h1>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Heatmap</Label>
          <Switch checked={heatmapMode} onCheckedChange={setHeatmapMode} data-testid="heatmap-toggle" />
        </div>
      </div>

      {/* Legend */}
      {heatmapMode ? (
        <div className="flex flex-wrap gap-2 text-xs">
          {[{c: AGE_COLORS.empty, l: 'Empty'}, {c: AGE_COLORS.fresh, l: '0-30d'}, {c: AGE_COLORS.normal, l: '31-60d'}, {c: AGE_COLORS.getting_old, l: '61-90d'}, {c: AGE_COLORS.slow, l: '91-120d'}, {c: AGE_COLORS.dead, l: '120d+'}, {c: AGE_COLORS.qc_hold, l: 'QC Hold'}].map((item) => (
            <div key={item.l} className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{backgroundColor: item.c}} />{item.l}</div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 text-xs">
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-gray-200" />Empty</div>
          {Object.entries(ROW_COLORS).map(([r, c]) => (
            <div key={r} className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{backgroundColor: c}} />Row {r}</div>
          ))}
        </div>
      )}

      {/* Bird's Eye View - Reversed rack order (R01 on RIGHT near entry) */}
      <Card className="border-0 shadow-sm overflow-x-auto">
        <CardContent className="p-4">
          <div className="relative min-w-[700px]">
            {/* Entry Arrow on RIGHT */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 text-[#1a2fce]">
              <ArrowRight size={18} />
              <span className="text-[10px] font-semibold">Entry</span>
            </div>

            {['A', 'B', 'C', 'D'].map((rowKey) => {
              const rowData = rows[rowKey];
              if (!rowData) return null;
              // REVERSED: Sort by rack_number DESCENDING so R01 is on the RIGHT
              const racks = Object.entries(rowData.racks).sort((a, b) => b[1].rack_number - a[1].rack_number);
              
              return (
                <React.Fragment key={rowKey}>
                  {rowKey === 'A' && <div className="text-[10px] text-gray-400 text-center mb-1">Rear Wall</div>}
                  <div className="flex items-end gap-0.5 mb-1">
                    <div className="w-12 text-xs font-bold shrink-0 pb-1" style={{ color: ROW_COLORS[rowKey] }}>Row {rowKey}</div>
                    <div className="flex gap-0.5 flex-1">
                      {racks.map(([rackCode, rackData]) => {
                        const occ = getRackOccupancy(rackCode);
                        const isTall = TALL_RACKS.includes(rackCode);
                        let bgColor;
                        if (heatmapMode) {
                          const rackPallets = rackData.slots.flatMap(s => palletsBySlot[s.slot_address] || []);
                          if (rackPallets.length === 0) bgColor = AGE_COLORS.empty;
                          else {
                            const oldestDays = Math.max(...rackPallets.map(p => {
                              if (p.status === 'qc_hold') return -1;
                              if (!p.date_placed) return 0;
                              return Math.floor((Date.now() - new Date(p.date_placed).getTime()) / 86400000);
                            }));
                            const hasQC = rackPallets.some(p => p.status === 'qc_hold');
                            bgColor = hasQC ? AGE_COLORS.qc_hold : getAgeColor(oldestDays);
                          }
                        } else {
                          bgColor = getOccupancyColor(occ.ratio, rowKey);
                        }

                        return (
                          <button
                            key={rackCode}
                            onClick={() => openRackPanel(rackCode)}
                            className="flex flex-col items-center justify-end rounded transition-all hover:ring-2 hover:ring-[#1a2fce]/50"
                            style={{
                              backgroundColor: bgColor,
                              minWidth: '30px',
                              // Taller height for 6-level racks (A01-A09)
                              height: isTall ? '48px' : '32px',
                              border: selectedRack === rackCode ? '2px solid #1a2fce' : '1px solid #dde3ed'
                            }}
                            data-testid={`rack-${rackCode}`}
                            title={`${rackCode}: ${occ.occupied}/${occ.total} slots occupied`}
                          >
                            <span className="text-[8px] font-mono font-bold pb-0.5" style={{ color: occ.ratio > 0.5 ? 'white' : '#374151' }}>{rackCode}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {rowKey === 'A' && <div className="text-[10px] text-center text-gray-400 my-1.5">Common Aisle (14'2")</div>}
                  {rowKey === 'B' && <div className="text-[10px] text-center text-gray-400 my-0.5">Back-to-back</div>}
                  {rowKey === 'C' && <div className="text-[10px] text-center text-gray-400 my-1.5">Secondary Aisle (15')</div>}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Rack Face View Sheet (Side Panel) */}
      <Sheet open={rackPanelOpen} onOpenChange={setRackPanelOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: ROW_COLORS[selectedRack?.[0]] + '20' }}>
                <Layers size={20} style={{ color: ROW_COLORS[selectedRack?.[0]] }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-lg">Rack {selectedRack}</span>
                  <Badge variant="outline" className="text-xs" style={{ borderColor: ROW_COLORS[selectedRack?.[0]], color: ROW_COLORS[selectedRack?.[0]] }}>
                    Row {selectedRack?.[0]}
                  </Badge>
                </div>
                {rackInfo && (
                  <div className="text-sm text-gray-500 font-normal mt-1">
                    {rackInfo.total} slots | {rackInfo.occupied} occupied | {rackInfo.total - rackInfo.occupied} empty
                  </div>
                )}
              </div>
            </SheetTitle>
          </SheetHeader>

          {rackInfo && (
            <div className="space-y-3">
              {/* Front Elevation View: L1 at bottom, L6/L4 at top; P1 leftmost */}
              <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                <Grid3X3 size={12} /> Front elevation view (facing rack from aisle)
              </div>
              
              {/* Render levels top to bottom (highest level first) */}
              {Array.from({ length: rackInfo.max_level }, (_, li) => rackInfo.max_level - li).map(level => (
                <div key={level} className="flex items-stretch gap-2">
                  <span className="text-[11px] text-gray-400 w-8 text-right shrink-0 pt-4 font-mono">L{level}</span>
                  <div className="flex gap-2 flex-1">
                    {/* P1 leftmost to P3 rightmost */}
                    {Array.from({ length: rackInfo.max_position }, (_, pi) => pi + 1).map(pos => {
                      const slotAddr = `${selectedRack}-L${level}-P${pos}`;
                      const slot = rackInfo.slots.find(s => s.slot_address === slotAddr);
                      if (!slot) return <div key={pos} className="flex-1 h-20 bg-gray-100 rounded-lg border border-dashed border-gray-200" />;

                      const slotPallets = palletsBySlot[slotAddr] || [];
                      let bgColor = '#f9fafb';
                      let textColor = '#9ca3af';
                      let borderColor = '#e5e7eb';

                      if (slotPallets.length > 0) {
                        const p = slotPallets[0];
                        if (p.status === 'qc_hold') {
                          bgColor = '#fef2f2';
                          borderColor = '#e8191a';
                          textColor = '#e8191a';
                        } else if (heatmapMode && p.date_placed) {
                          const days = Math.floor((Date.now() - new Date(p.date_placed).getTime()) / 86400000);
                          bgColor = getAgeColor(days) + '30';
                          borderColor = getAgeColor(days);
                          textColor = '#1f2937';
                        } else {
                          const cat = p.items?.[0]?.category;
                          const catColor = CATEGORY_COLORS[cat] || '#1a2fce';
                          bgColor = catColor + '15';
                          borderColor = catColor;
                          textColor = '#1f2937';
                        }
                      }

                      return (
                        <button
                          key={pos}
                          onClick={() => {
                            if (slotPallets.length > 0) {
                              navigate(`/pallet/${slotPallets[0].pallet_ref}`);
                            } else {
                              setSelectedSlot({ slot, pallets: slotPallets });
                            }
                          }}
                          className="flex-1 min-h-[80px] rounded-lg flex flex-col items-center justify-center p-2 transition-all hover:scale-[1.02] hover:shadow-md"
                          style={{ backgroundColor: bgColor, border: `2px solid ${borderColor}`, color: textColor }}
                          data-testid={`slot-${slotAddr}`}
                        >
                          <span className="text-[10px] text-gray-400 font-mono mb-1">{slotAddr.split('-').slice(1).join('-')}</span>
                          {slotPallets.length > 0 ? (
                            <>
                              <span className="text-xs font-mono font-bold">{slotPallets[0].pallet_ref}</span>
                              {slotPallets[0].status === 'qc_hold' && (
                                <Badge className="text-[8px] px-1 py-0 mt-1 bg-red-600 text-white">QC HOLD</Badge>
                              )}
                              {slotPallets.length > 1 && (
                                <span className="text-[10px] text-gray-500 mt-0.5">+{slotPallets.length - 1} more</span>
                              )}
                              <span className="text-[9px] text-gray-500 mt-1 truncate max-w-full">
                                {slotPallets[0].items?.[0]?.category || ''}
                              </span>
                            </>
                          ) : (
                            <span className="text-[10px] text-gray-400">Empty</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Position legend */}
              <div className="flex items-center justify-center gap-4 text-[10px] text-gray-400 pt-2 border-t border-gray-100">
                <span>P1 (left)</span>
                <span>P2 (middle)</span>
                {rackInfo.max_position >= 3 && <span>P3 (right)</span>}
                {rackInfo.max_position >= 4 && <span>P4</span>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Empty Slot Action Dialog */}
      <Dialog open={!!selectedSlot && selectedSlot.pallets?.length === 0} onOpenChange={() => setSelectedSlot(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin size={16} className="text-[#1a2fce]" />
              {selectedSlot?.slot?.slot_address}
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-6 text-gray-500">
            <Package size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm mb-4">This slot is empty</p>
            <Button 
              onClick={() => {
                setSelectedSlot(null);
                setRackPanelOpen(false);
                navigate('/pack');
              }}
              style={{ backgroundColor: '#1a2fce' }}
            >
              Start New Packing Session
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
